import { useMemo } from "react";
import { useAppSelector } from "@app/hooks";
import { selectProjectsFilters } from "@app/features/project/projectSlice";
import { useContextAwareGetAllTags, useGetProjectsInfiniteQuery } from "@hooks/index";
import { FastProject } from "@typedefs/interfaces";

const ITEMS_PER_PAGE = 10;

interface UseProjectDataReturn {
	tableData: FastProject[];
	isError: boolean;
	isLoading: boolean;
	projectsCount: number;
	handleNext: () => void;
	handlePrev: () => void;
	refetchProjects: () => void;
	ITEMS_PER_PAGE: number;
}

// this hook is used to get the projects data
export const useProjectData = (): UseProjectDataReturn => {
	const { data: tags } = useContextAwareGetAllTags();
	const { showFinalized, tags: tagsIds, showTrashbin } = useAppSelector(selectProjectsFilters);
	const tagNames = tagsIds && tags ? tags?.filter(tag => tagsIds.includes(tag.id)).map(tag => tag.name) : [];

	const {
		data: projectsRes,
		isLoading,
		isFetching,
		isError,
		refetch,
		hasNextPage,
		hasPreviousPage,
		fetchNextPage,
		fetchPreviousPage,
	} = useGetProjectsInfiniteQuery({ showFinalized, tags: tagNames, showTrashbin, page_size: ITEMS_PER_PAGE });

	const { projects, projectsCount } = useMemo(() => {
		const projects = projectsRes?.pages.flatMap(page => page.results) ?? [];
		const projectsCount = projectsRes?.pages[0]?.count ?? 0;
		return { projects, projectsCount };
	}, [projectsRes]);

	const handleNext = () => {
		if (hasNextPage) {
			fetchNextPage();
		}
	};

	const handlePrev = () => {
		if (hasPreviousPage) {
			fetchPreviousPage();
		}
	};

	const tableData = isLoading ? Array(2).fill({}) : projects as FastProject[];

	return {
		tableData,
		isError,
		isLoading: isLoading || isFetching,
		projectsCount,
		handleNext,
		handlePrev,
		refetchProjects: refetch,
		ITEMS_PER_PAGE
	};
}; 